import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { AfterViewInit, Directive, ElementRef, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CanDisable, mixinDisabled } from '@shared/common';

export const NDT_BUTTON_INPUTS = ['disabled'];

export const NDT_BUTTON_HOST = {
  '[attr.disabled]': 'disabled || null',
  '[class.ndt-button-base]': 'true',
};

const HOST_SELECTOR_CLASS_PAIR: { selector: string; mdcClasses: string[] }[] = [
  {
    selector: 'ndt-primary-button',
    mdcClasses: ['ndt-primary-button'],
  },
  {
    selector: 'ndt-secondary-button',
    mdcClasses: ['ndt-secondary-button'],
  },
  {
    selector: 'ndt-tertiary-button',
    mdcClasses: ['ndt-tertiary-button'],
  },
  {
    selector: 'ndt-fab',
    mdcClasses: ['ndt-fab'],
  },
  {
    selector: 'ndt-mini-fab',
    mdcClasses: ['ndt-mini-fab'],
  },
  {
    selector: 'ndt-icon-button',
    mdcClasses: ['ndt-icon-button'],
  },
  {
    selector: 'ndt-anchor',
    mdcClasses: ['ndt-anchor'],
  },
];

const NDtButtonMixin = mixinDisabled(
  class {
    constructor(readonly elementRef: ElementRef<HTMLAnchorElement | HTMLButtonElement>) {}
  },
);

@Directive()
export class NDtButtonBase extends NDtButtonMixin implements CanDisable, AfterViewInit, OnDestroy {
  private readonly focusMonitor = inject(FocusMonitor);

  constructor(
    override readonly elementRef: ElementRef<HTMLAnchorElement | HTMLButtonElement>,
    readonly ngZone: NgZone,
  ) {
    super(elementRef);
    const { classList } = elementRef.nativeElement as HTMLElement;

    for (const pair of HOST_SELECTOR_CLASS_PAIR) {
      if (this.hasHostAttributes(pair.selector)) {
        pair.mdcClasses.forEach((className: string) => {
          classList.add(className);
        });
      }
    }
  }

  ngAfterViewInit() {
    this.focusMonitor.monitor(this.elementRef, true);
  }

  ngOnDestroy() {
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  focus(origin: FocusOrigin = 'program', options?: FocusOptions): void {
    if (origin) {
      this.focusMonitor.focusVia(this.elementRef.nativeElement, origin, options);
    } else {
      this.elementRef.nativeElement.focus(options);
    }
  }

  private hasHostAttributes(...attributes: string[]) {
    return attributes.some((attribute) => this.elementRef.nativeElement.hasAttribute(attribute));
  }
}

export const NDT_ANCHOR_INPUTS = ['disabled', 'tabIndex'];

export const NDT_ANCHOR_HOST = {
  '[attr.disabled]': 'disabled || null',

  // Note that we ignore the user-specified tabindex when it's disabled for
  // consistency with the `ndt-button` applied on native buttons where even
  // though they have an index, they're not tabbable.
  '[attr.tabindex]': 'disabled ? -1 : tabIndex',
  '[attr.aria-disabled]': 'disabled.toString()',
  '[class.ndt-button-base]': 'true',
};

@Directive()
export class NDtAnchorBase extends NDtButtonBase implements OnInit, OnDestroy {
  tabIndex: number;

  constructor(
    override readonly elementRef: ElementRef<HTMLAnchorElement | HTMLButtonElement>,
    override readonly ngZone: NgZone,
  ) {
    super(elementRef, ngZone);
  }

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.elementRef.nativeElement.addEventListener('click', this.haltDisabledEvents);
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.elementRef.nativeElement.removeEventListener('click', this.haltDisabledEvents);
  }

  private haltDisabledEvents = (event: Event): void => {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };
}
